import { useApiClient } from '@m43/core';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useCallback, useState } from 'react';
import { DexResultsStore } from './dex-results-store';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { useIntegratedStreams } from '../../common/hooks/use-integrated-streams';
import { useDexResultsBroadcastListener } from './use-dex-results-broadcast-listener';
import { DexResultsResource, DexResultsView } from '@mark43/dex-api';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { electronBridge } from '../../electron-bridge/electron-bridge';
import { usePlaySound } from '../../sounds/play-sound-provider';
import { useSelectDexResults } from './use-select-dex-results';

export type IntegratedDexResultsReturn = {
    dexResultsStore: DexResultsStore;
    getDexResultsQuery: UseQueryResult<DexResultsView[] | undefined, unknown>;
};

export function useIntegratedDexResults(): IntegratedDexResultsReturn {
    const { isElectron, isCad, isFirstResponder } = useApplicationType();
    const apiClient = useApiClient();
    const {
        dataExchange,
        dexNextEnabled,
        playAlertOnHotHitResultForOfficerDexQuery,
        firstResponderPlayAlertOnHotHitDexResult,
    } = useEnabledFeatures();
    const { userProfileView, latestSeenDexIncomingMessageId } = useBootstrapState();
    const [dexResultsStore] = useState<DexResultsStore>(
        () => new DexResultsStore(userProfileView.userId, latestSeenDexIncomingMessageId)
    );
    const { selectDexResultsSummary } = useSelectDexResults(dexResultsStore);
    const { playSound } = usePlaySound();
    const fetchDexResultsViews = useCallback(
        (outgoingMessageIds: number[]) => {
            return apiClient
                .request<DexResultsResource.GetDexResultsViewForOutgoingMessageIds>({
                    service: 'DEX',
                    method: 'POST',
                    url: 'dex_results',
                    data: {
                        outgoingMessageIds,
                    },
                })
                .then((response) => response.data.data)
                .then(async (dexResultsViews) => {
                    if (dexResultsViews) {
                        const {
                            hasNewHotHitFromOtherUserQuery,
                            hasNewHotHitFromOwnQuery,
                            outgoingMessageIdsWithNewSummaryViewFromOwnQuery,
                        } = dexResultsStore.upsertDexResultsViews(
                            dexResultsViews,
                            isFirstResponder
                        );
                        if (
                            isCad &&
                            playAlertOnHotHitResultForOfficerDexQuery &&
                            hasNewHotHitFromOtherUserQuery
                        ) {
                            if (isElectron) {
                                if (await electronBridge?.requestToPlayAudioAlert()) {
                                    playSound('dex-hot-hit-alert');
                                }
                            } else {
                                playSound('dex-hot-hit-alert');
                            }
                        } else if (
                            isFirstResponder &&
                            firstResponderPlayAlertOnHotHitDexResult &&
                            hasNewHotHitFromOwnQuery
                        ) {
                            playSound('dex-hot-hit-alert');
                        }
                        if (
                            !dexResultsStore.getSelectedResultId() &&
                            !dexResultsStore.getSelectedSummaryId() &&
                            outgoingMessageIdsWithNewSummaryViewFromOwnQuery.length > 0
                        ) {
                            const newestOutgoingMessageId = Math.max(
                                ...outgoingMessageIdsWithNewSummaryViewFromOwnQuery
                            );
                            selectDexResultsSummary(newestOutgoingMessageId);
                        }
                    }
                });
        },
        [
            apiClient,
            dexResultsStore,
            playAlertOnHotHitResultForOfficerDexQuery,
            firstResponderPlayAlertOnHotHitDexResult,
            isElectron,
            isCad,
            isFirstResponder,
            playSound,
            selectDexResultsSummary,
        ]
    );

    const getDexResultsQuery = useQuery(
        ['dex-results'],
        () => {
            return apiClient
                .request<DexResultsResource.GetDexResultsViewsForActingUserInbox>({
                    service: 'DEX',
                    method: 'GET',
                    url: 'dex_results/user',
                })
                .then((response) => response.data.data);
        },
        {
            onSuccess(dexResultsViews) {
                dexResultsStore.setDexResultsViews(dexResultsViews, isFirstResponder);
            },
            enabled: false,
        }
    );

    useDexResultsBroadcastListener(dexResultsStore, getDexResultsQuery);

    useIntegratedStreams({
        enabled: dataExchange && !dexNextEnabled,
        streamNames: ['dex_nudge'],
        query: getDexResultsQuery,
        onMessage: (message) => [message.payload],
        promiseBufferCallback: (inputs) => {
            return fetchDexResultsViews(inputs);
        },
    });

    return {
        dexResultsStore,
        getDexResultsQuery,
    };
}
